/**
 * @fileoverview Excel Export Hook for UniversalTable
 * 
 * Provides functionality to export selected table rows to Excel format.
 * Automatically filters out action columns and formats data appropriately.
 * 
 * @module UniversalTable/useExcelExport
 */

import { useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import type { ColumnDefinition } from '../types/ColumnConfig';

interface ExcelExportOptions {
  filename?: string;
  sheetName?: string;
  selectedRowsOnly?: boolean;
  excludeColumns?: string[];
  columnMapping?: Record<string, string>;
  currencyColumns?: string[]; // Column IDs that should be formatted as currency
  percentageColumns?: string[]; // Column IDs that should be formatted as percentage
  freezeColumns?: number; // Number of columns to freeze from left (in addition to header row)
}

interface ExcelExportHook {
  exportToExcel: (
    rows: any[],
    columns: ColumnDefinition[],
    selectedRows?: any[],
    options?: ExcelExportOptions
  ) => Promise<void>;
  isExporting: boolean;
}

/**
 * Hook for exporting table data to Excel
 * 
 * Features:
 * - Automatic filtering of action columns (checkbox, actions, comm, insurance)
 * - Smart data extraction from complex objects
 * - Support for selected rows only or all rows
 * - Customizable filename and sheet name
 * - Loading state management
 * - Error handling with toast notifications
 * 
 * @example
 * ```tsx
 * const { exportToExcel, isExporting } = useExcelExport();
 * 
 * const handleExport = () => {
 *   exportToExcel(
 *     filteredRows,
 *     columnsConfig.columns,
 *     selectedRows,
 *     { 
 *       filename: 'helpdesk-tickets',
 *       selectedRowsOnly: true 
 *     }
 *   );
 * };
 * ```
 */
export const useExcelExport = (): ExcelExportHook => {
  const [isExporting, setIsExporting] = useState(false);
  const { session } = useSession();

  /**
   * Export table data to Excel
   * 
   * @param rows - All table rows
   * @param columns - Column definitions from table config
   * @param selectedRows - Currently selected rows (optional)
   * @param options - Export options
   */
  const exportToExcel = async (
    rows: any[],
    columns: ColumnDefinition[],
    selectedRows: any[] = [],
    options: ExcelExportOptions = {}
  ) => {
    const {
      filename = 'table-export',
      sheetName = 'Data',
      selectedRowsOnly = false,
      excludeColumns = [],
      columnMapping = {},
      currencyColumns = [],
      percentageColumns = [],
      freezeColumns = 0
    } = options;

    setIsExporting(true);

    try {
      // Determine which rows to export
      const rowsToExport = selectedRowsOnly && selectedRows.length > 0 
        ? selectedRows 
        : rows;

      if (rowsToExport.length === 0) {
        toast.warning('Geen gegevens om te exporteren');
        return;
      }

      // Prepare column data for export
      const exportColumns = columns.map(col => ({
        id: col.id,
        label: typeof col.label === 'string' ? col.label : col.id,
        sortKey: col.sortKey,
        isExportable: determineIfExportable(col, excludeColumns),
        dataField: columnMapping[col.id] || col.sortKey || col.id // Map column ID to actual data field
      }));

      // Make API request
      const response = await fetchWithAuth(
        `${API_URL}/excel/export-universal-table`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rows: rowsToExport,
            columns: exportColumns,
            filename,
            sheetName,
            currencyColumns,
            percentageColumns,
            freezeColumns
          }),
        },
        session
      );

      if (response.ok) {
        // Handle file download
        await downloadExcelFile(response, filename);
        
        const exportedCount = rowsToExport.length;
        const exportType = selectedRowsOnly && selectedRows.length > 0 ? 'geselecteerde' : 'alle';
        toast.success(`${exportedCount} ${exportType} rijen geëxporteerd naar Excel`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Fout bij het exporteren naar Excel');
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportToExcel,
    isExporting
  };
};

/**
 * Determine if a column should be exportable based on its configuration
 */
function determineIfExportable(column: ColumnDefinition, excludeColumns: string[] = []): boolean {
  // Check explicit exclusion list first
  if (excludeColumns.includes(column.id)) {
    return false;
  }
  
  // Known action column IDs that should be excluded (fallback)
  const defaultActionColumnIds = ['checkbox', 'actions', 'comm', 'insurance'];
  if (defaultActionColumnIds.includes(column.id)) {
    return false;
  }
  
  // Columns with sortKey are typically data columns
  if (column.sortKey) {
    return true;
  }
  
  // Default to exportable for other columns
  return true;
}

/**
 * Handle the Excel file download from the response
 */
async function downloadExcelFile(response: Response, filename: string): Promise<void> {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  
  // Create temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
